import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldAlert, Plus, Pencil, Trash2, Sparkles, Building2, Search } from "lucide-react";

const CONTRIBUTION_TYPES = [
  { value: "percentage_of_gross", label: "Percentage of gross" },
  { value: "fixed_amount", label: "Fixed amount (€)" },
  { value: "balance_to_combined_target", label: "Balance to combined target" },
  { value: "manual", label: "Manual (from payroll deduction)" },
  { value: "none", label: "None" },
];

const ALLOCATION_METHODS = [
  { value: "month_end_true_up", label: "Month-end true-up (default)" },
  { value: "equal_weekly_allocation", label: "Equal weekly allocation" },
  { value: "actual_payroll_weeks_in_month", label: "Actual payroll weeks in month" },
  { value: "manual", label: "Manual (no auto allocation)" },
];

function emptyForm(employeeId) {
  return {
    employee_id: employeeId || "",
    pension_enabled: true,
    pension_provider: "Zurich",
    scheme_name: "",
    effective_start_date: new Date().toISOString().slice(0, 10),
    effective_end_date: "",
    employer_contribution_type: "percentage_of_gross",
    employer_contribution_percentage: 1.5,
    employer_contribution_fixed_amount: "",
    employee_contribution_type: "manual",
    employee_contribution_percentage: "",
    employee_contribution_fixed_amount: "",
    combined_target_enabled: false,
    combined_target_amount: "",
    combined_target_frequency: "monthly",
    allocation_method: "month_end_true_up",
    voluntary_inclusion: false,
    voluntary_inclusion_reason: "",
    notes: "",
  };
}

function RuleForm({ form, onChange, employees, isNew, lockedEmployee }) {
  const set = (k, v) => onChange({ ...form, [k]: v });
  return (
    <div className="space-y-4">
      {lockedEmployee ? (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3" data-testid="rule-employee-locked">
          <div className="tiny-label">Employee</div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span className="font-semibold text-base">{lockedEmployee.full_name}</span>
            {lockedEmployee.employee_id && (
              <span className="font-mono text-xs text-muted-foreground">· {lockedEmployee.employee_id}</span>
            )}
          </div>
        </div>
      ) : (
        <div>
          <Label>Employee</Label>
          <Select value={form.employee_id} onValueChange={(v) => set("employee_id", v)} disabled={!isNew}>
            <SelectTrigger data-testid="rule-employee"><SelectValue placeholder="Pick employee" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.full_name}{e.employee_id ? ` · ${e.employee_id}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Switch checked={form.pension_enabled} onCheckedChange={(v) => set("pension_enabled", v)} id="pension-enabled" data-testid="rule-pension-enabled" />
        <Label htmlFor="pension-enabled">Pension enabled</Label>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label>Provider</Label>
          <Input value={form.pension_provider} onChange={(e) => set("pension_provider", e.target.value)} />
        </div>
        <div>
          <Label>Scheme name (optional)</Label>
          <Input value={form.scheme_name} onChange={(e) => set("scheme_name", e.target.value)} placeholder="e.g. GBBS Standard" />
        </div>
        <div></div>
        <div>
          <Label>Effective start *</Label>
          <Input type="date" value={form.effective_start_date} onChange={(e) => set("effective_start_date", e.target.value)} data-testid="rule-start" />
        </div>
        <div>
          <Label>Effective end (blank = open)</Label>
          <Input type="date" value={form.effective_end_date} onChange={(e) => set("effective_end_date", e.target.value)} data-testid="rule-end" />
        </div>
      </div>

      <div className="border-t border-border pt-3 space-y-3">
        <div className="tiny-label">Employer contribution</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label>Type</Label>
            <Select value={form.employer_contribution_type} onValueChange={(v) => set("employer_contribution_type", v)}>
              <SelectTrigger data-testid="rule-er-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTRIBUTION_TYPES.filter((t) => t.value !== "balance_to_combined_target").map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.employer_contribution_type === "percentage_of_gross" && (
            <div>
              <Label>Percentage (%)</Label>
              <Input type="number" step="0.01" value={form.employer_contribution_percentage} onChange={(e) => set("employer_contribution_percentage", e.target.value)} data-testid="rule-er-pct" />
            </div>
          )}
          {form.employer_contribution_type === "fixed_amount" && (
            <div>
              <Label>Fixed amount €</Label>
              <Input type="number" step="0.01" value={form.employer_contribution_fixed_amount} onChange={(e) => set("employer_contribution_fixed_amount", e.target.value)} />
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-border pt-3 space-y-3">
        <div className="tiny-label">Employee contribution</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label>Type</Label>
            <Select value={form.employee_contribution_type} onValueChange={(v) => set("employee_contribution_type", v)}>
              <SelectTrigger data-testid="rule-ee-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTRIBUTION_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {form.employee_contribution_type === "percentage_of_gross" && (
            <div>
              <Label>Percentage (%)</Label>
              <Input type="number" step="0.01" value={form.employee_contribution_percentage} onChange={(e) => set("employee_contribution_percentage", e.target.value)} />
            </div>
          )}
          {form.employee_contribution_type === "fixed_amount" && (
            <div>
              <Label>Fixed amount €</Label>
              <Input type="number" step="0.01" value={form.employee_contribution_fixed_amount} onChange={(e) => set("employee_contribution_fixed_amount", e.target.value)} />
            </div>
          )}
          {form.employee_contribution_type === "manual" && (
            <div className="text-xs text-muted-foreground pt-7">
              Expected employee deduction will use the payroll-recorded amount.
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-border pt-3 space-y-3">
        <div className="flex items-center gap-2">
          <Switch checked={form.combined_target_enabled} onCheckedChange={(v) => set("combined_target_enabled", v)} id="combined-toggle" data-testid="rule-combined-toggle" />
          <Label htmlFor="combined-toggle" className="flex items-center gap-2">Combined target <Sparkles className="w-3.5 h-3.5 text-amber-500" /></Label>
        </div>
        {form.combined_target_enabled && (
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label>Amount €</Label>
              <Input type="number" step="0.01" value={form.combined_target_amount} onChange={(e) => set("combined_target_amount", e.target.value)} data-testid="rule-target-amount" />
            </div>
            <div>
              <Label>Frequency</Label>
              <Select value={form.combined_target_frequency} onValueChange={(v) => set("combined_target_frequency", v)}>
                <SelectTrigger data-testid="rule-target-freq"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Allocation method</Label>
              <Select value={form.allocation_method} onValueChange={(v) => set("allocation_method", v)}>
                <SelectTrigger data-testid="rule-allocation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ALLOCATION_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-3 text-xs text-muted-foreground">
              When employee type is <span className="font-mono">balance_to_combined_target</span>, the expected employee contribution will be <span className="font-semibold text-foreground">target − employer</span> for each month.
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-border pt-3 space-y-3">
        <div className="flex items-center gap-2">
          <Switch checked={form.voluntary_inclusion} onCheckedChange={(v) => set("voluntary_inclusion", v)} id="voluntary-toggle" />
          <Label htmlFor="voluntary-toggle">Voluntary inclusion (under 18 or otherwise opted in)</Label>
        </div>
        {form.voluntary_inclusion && (
          <Input value={form.voluntary_inclusion_reason} onChange={(e) => set("voluntary_inclusion_reason", e.target.value)} placeholder="Reason (optional)" />
        )}
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Free-text notes (audit only)" />
      </div>
    </div>
  );
}

export default function PensionRulesPage() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showStandard, setShowStandard] = useState(true);
  const [showEnhanced, setShowEnhanced] = useState(true);
  const [showDefault, setShowDefault] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // rule object when editing, null when creating
  const [form, setForm] = useState(emptyForm());
  const [lockedEmployee, setLockedEmployee] = useState(null); // {id, full_name, employee_id} when opened from a row

  const load = async () => {
    setLoading(true);
    try {
      const [list, emps] = await Promise.all([
        api.get("/pension-rules/employees-with-rules"),
        api.get("/employees"),
      ]);
      setItems(list.data);
      setEmployees(emps.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((e) => {
    const matchesSearch = !search || (
      (e.full_name || "").toLowerCase().includes(search.toLowerCase())
      || (e.employee_id || "").toLowerCase().includes(search.toLowerCase())
    );
    if (!matchesSearch) return false;
    if (e.is_default_policy && !showDefault) return false;
    if (e.is_enhanced && !showEnhanced) return false;
    if (!e.is_default_policy && !e.is_enhanced && !showStandard) return false;
    return true;
  }), [items, search, showStandard, showEnhanced, showDefault]);

  const resolveEmployee = (employee_id) => {
    if (!employee_id) return null;
    const fromItems = items.find((x) => x.id === employee_id);
    if (fromItems) return { id: fromItems.id, full_name: fromItems.full_name, employee_id: fromItems.employee_id };
    const fromEmps = employees.find((x) => x.id === employee_id);
    if (fromEmps) return { id: fromEmps.id, full_name: fromEmps.full_name, employee_id: fromEmps.employee_id };
    return null;
  };

  const openNew = (employee_id) => {
    setEditing(null);
    setForm(emptyForm(employee_id));
    setLockedEmployee(resolveEmployee(employee_id));
    setDialogOpen(true);
  };

  const openEdit = (rule) => {
    setEditing(rule);
    setForm({
      ...emptyForm(rule.employee_id),
      ...rule,
      effective_end_date: rule.effective_end_date || "",
      employer_contribution_percentage: rule.employer_contribution_percentage ?? "",
      employer_contribution_fixed_amount: rule.employer_contribution_fixed_amount ?? "",
      employee_contribution_percentage: rule.employee_contribution_percentage ?? "",
      employee_contribution_fixed_amount: rule.employee_contribution_fixed_amount ?? "",
      combined_target_amount: rule.combined_target_amount ?? "",
      combined_target_frequency: rule.combined_target_frequency || "monthly",
      voluntary_inclusion_reason: rule.voluntary_inclusion_reason || "",
      notes: rule.notes || "",
      scheme_name: rule.scheme_name || "",
    });
    setLockedEmployee(resolveEmployee(rule.employee_id));
    setDialogOpen(true);
  };

  const submit = async () => {
    const payload = {
      ...form,
      effective_end_date: form.effective_end_date || null,
      scheme_name: form.scheme_name || null,
      employer_contribution_percentage: form.employer_contribution_percentage === "" ? null : Number(form.employer_contribution_percentage),
      employer_contribution_fixed_amount: form.employer_contribution_fixed_amount === "" ? null : Number(form.employer_contribution_fixed_amount),
      employee_contribution_percentage: form.employee_contribution_percentage === "" ? null : Number(form.employee_contribution_percentage),
      employee_contribution_fixed_amount: form.employee_contribution_fixed_amount === "" ? null : Number(form.employee_contribution_fixed_amount),
      combined_target_amount: form.combined_target_amount === "" ? null : Number(form.combined_target_amount),
      combined_target_frequency: form.combined_target_enabled ? form.combined_target_frequency : null,
      voluntary_inclusion_reason: form.voluntary_inclusion_reason || null,
      notes: form.notes || null,
    };
    try {
      if (editing) {
        await api.patch(`/pension-rules/${editing.id}`, payload);
        toast.success("Rule updated");
      } else {
        await api.post("/pension-rules", payload);
        toast.success("Rule created");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete this rule (effective ${rule.effective_start_date} → ${rule.effective_end_date || "open"})?`)) return;
    try {
      await api.delete(`/pension-rules/${rule.id}`);
      toast.success("Rule deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="pension-rules-page">
      <div>
        <div className="tiny-label">Phase 2 · employee-level rules · admin only</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Employee pension rules</h1>
        <p className="text-muted-foreground mt-1 max-w-3xl">
          Per-employee, effective-dated pension rules. Standard employees use the GBBS default (1.5% employer + payroll-driven employee deduction). Enhanced contributors (combined target) can have a custom monthly/weekly target — the employee share auto-balances to hit it.
        </p>
      </div>

      <Alert className="border-blue-200 bg-blue-50 text-blue-800">
        <ShieldAlert className="w-4 h-4" />
        <AlertTitle>Reconciliation uses these rules</AlertTitle>
        <AlertDescription>
          When you run the Zurich reconciliation, the engine looks up each employee's active rule for the reconciliation month and uses it to compute the expected contributions. Employees with no rule on file use the GBBS default policy.
        </AlertDescription>
      </Alert>

      <Card className="border-border shadow-none">
        <CardContent className="p-4 grid sm:grid-cols-12 gap-3 items-end">
          <div className="sm:col-span-5">
            <Label>Search</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or employee id" data-testid="rules-search" />
            </div>
          </div>
          <div className="sm:col-span-5 flex items-center gap-4">
            <div className="flex items-center gap-2"><Switch checked={showStandard} onCheckedChange={setShowStandard} id="show-std" /><Label htmlFor="show-std" className="text-sm">Standard rule</Label></div>
            <div className="flex items-center gap-2"><Switch checked={showEnhanced} onCheckedChange={setShowEnhanced} id="show-enh" /><Label htmlFor="show-enh" className="text-sm">Enhanced</Label></div>
            <div className="flex items-center gap-2"><Switch checked={showDefault} onCheckedChange={setShowDefault} id="show-def" /><Label htmlFor="show-def" className="text-sm">Default policy</Label></div>
          </div>
          <div className="sm:col-span-2">
            <Button className="w-full h-10" onClick={() => openNew("")} data-testid="rules-add-btn"><Plus className="w-4 h-4 mr-1" /> Add rule</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border shadow-none">
        <div className="overflow-x-auto app-scroll">
          <table className="w-full text-sm" data-testid="rules-table">
            <thead className="bg-muted/50">
              <tr className="text-left tiny-label">
                <th className="px-3 py-3">Employee</th>
                <th className="px-3 py-3">Active rule</th>
                <th className="px-3 py-3">Effective</th>
                <th className="px-3 py-3 text-right">Employer</th>
                <th className="px-3 py-3 text-right">Employee</th>
                <th className="px-3 py-3">Combined target</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3 text-right">History</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const r = e.active_rule;
                const employer = r?.employer_contribution_type === "percentage_of_gross"
                  ? `${r.employer_contribution_percentage?.toFixed(2)}%`
                  : r?.employer_contribution_type === "fixed_amount"
                    ? `€${r.employer_contribution_fixed_amount?.toFixed(2)}`
                    : r?.employer_contribution_type || "—";
                const employee = r?.employee_contribution_type === "percentage_of_gross"
                  ? `${r.employee_contribution_percentage?.toFixed(2)}%`
                  : r?.employee_contribution_type === "fixed_amount"
                    ? `€${r.employee_contribution_fixed_amount?.toFixed(2)}`
                    : r?.employee_contribution_type === "balance_to_combined_target"
                      ? "balance"
                      : (r?.employee_contribution_type || "manual");
                const target = r?.combined_target_enabled
                  ? `€${r.combined_target_amount?.toFixed(2)} ${r.combined_target_frequency}`
                  : "—";
                return (
                  <tr key={e.id} className="border-t border-border" data-testid={`rules-row-${e.id}`}>
                    <td className="px-3 py-3"><div className="font-medium">{e.full_name}</div><div className="text-xs text-muted-foreground">{e.employee_id || "—"}</div></td>
                    <td className="px-3 py-3">
                      {e.is_default_policy ? (
                        <Badge variant="outline" className="text-slate-700 border-slate-300 bg-slate-50">Default (1.5%)</Badge>
                      ) : e.is_enhanced ? (
                        <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50"><Sparkles className="w-3 h-3 mr-1" /> Enhanced</Badge>
                      ) : (
                        <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">Custom rule</Badge>
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs">
                      {r ? `${r.effective_start_date} → ${r.effective_end_date || "open"}` : "—"}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">{r ? employer : "1.5%"}</td>
                    <td className="px-3 py-3 text-right font-mono">{r ? employee : "manual"}</td>
                    <td className="px-3 py-3 font-mono text-xs">{target}</td>
                    <td className="px-3 py-3 text-xs">{r?.pension_provider || "Zurich"}</td>
                    <td className="px-3 py-3 text-right font-mono">{e.rule_count}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex gap-2">
                        {r && !e.is_default_policy && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`rules-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="outline" onClick={() => remove(r)} data-testid={`rules-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </>
                        )}
                        <Button size="sm" onClick={() => openNew(e.id)} data-testid={`rules-add-for-${e.id}`}><Plus className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                  {loading ? "Loading…" : "No employees match the current filter."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit pension rule" : "Create pension rule"}
              {lockedEmployee && (
                <span className="text-muted-foreground font-normal"> — {lockedEmployee.full_name}</span>
              )}
            </DialogTitle>
            <DialogDescription>Effective-dated rule that drives the expected employer and employee pension contributions for this employee.</DialogDescription>
          </DialogHeader>
          <RuleForm form={form} onChange={setForm} employees={employees} isNew={!editing} lockedEmployee={lockedEmployee} />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!form.employee_id || !form.effective_start_date} data-testid="rule-submit">
              {editing ? "Save changes" : "Create rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
